<script module lang="ts">
	const REST_STOP_FACILITY_ID = {
		NURSERY: 'nursery',
		PLAYGROUND: 'playground',
		PICNIC_TABLE: 'picnic_table',
		RESTAURANT: 'restaurant',
		DOG_PARK: 'dog_park',
		SHOP: 'shop',
	} as const;

	export type RestStopFacilityType =
		(typeof REST_STOP_FACILITY_ID)[keyof typeof REST_STOP_FACILITY_ID];
</script>

<script lang="ts">
	import kleberPatternUrl from '$assets/pois/kleber_pattern.webp';
	import * as m from '$paraglide/messages';

	interface Props {
		sponsorId?: 'kleber';
		headline?: string;
		facilities: string[];
	}

	const { sponsorId, headline, facilities }: Props = $props();

	const restStopFacilityIds: RestStopFacilityType[] = $derived(
		facilities.filter((facility): facility is RestStopFacilityType =>
			Object.values(REST_STOP_FACILITY_ID).includes(facility as RestStopFacilityType),
		),
	);

	const restStopFacility: Record<
		RestStopFacilityType,
		{
			iconClass: string;
			text: string;
		}
	> = {
		[REST_STOP_FACILITY_ID.NURSERY]: {
			iconClass: 'icon-baby-changing-station',
			text: m.restStopService__nursery(),
		},
		[REST_STOP_FACILITY_ID.PLAYGROUND]: {
			iconClass: 'icon-play-area',
			text: m.restStopService__playGround(),
		},
		[REST_STOP_FACILITY_ID.PICNIC_TABLE]: {
			iconClass: 'icon-picnic',
			text: m.restStopService__picnicTable(),
		},
		[REST_STOP_FACILITY_ID.RESTAURANT]: {
			iconClass: 'icon-restaurant',
			text: m.restStopService__restaurant(),
		},
		[REST_STOP_FACILITY_ID.DOG_PARK]: {
			iconClass: 'icon-pet-supplies',
			text: m.restStopService__dogArea(),
		},
		[REST_STOP_FACILITY_ID.SHOP]: {
			iconClass: 'icon-storefront',
			text: m.restStopService__shop(),
		},
	};
</script>

<section
	class="facilities-container relative py-[var(--offset-height)]"
	style="--offset-height: 25px; --kleber-pattern-url: url('{kleberPatternUrl}');"
>
	{#if sponsorId || headline}
		<header class="flex flex-col items-center max-w-300px px-2 py-2 mx-auto">
			{#if sponsorId === 'kleber'}
				<div class="flex justify-center">
					<svg class="h-6.5 w-auto" viewBox="0 0 62 18">
						<path
							d="M41.7967 6.29698C42.1241 6.42607 42.3473 6.49219 42.5705 6.49219C42.7937 6.49219 43.0045 6.42229 43.2847 6.27809L53.3287 1.24554L50.654 0.198355C50.3366 0.0755638 50.106 0 49.8828 0C49.6596 0 49.4488 0.0755638 49.1685 0.214097L39.1245 5.25168L41.7967 6.29698Z"
							fill="#E30521"
						/>
						<path
							d="M50.21 6.04874C50.5299 6.17468 50.7599 6.2471 50.9831 6.2471C51.2063 6.2471 51.4221 6.17783 51.6974 6.03615L57.0294 3.37694C57.2079 3.28878 57.3053 3.1471 57.3053 3.01171C57.3053 2.86058 57.2079 2.72835 56.9996 2.64649L54.4421 1.6333L47.6357 5.03745L50.21 6.04497"
							fill="#E30521"
						/>
						<path
							d="M35.4523 3.84126L38.011 4.85507L44.8167 1.4547L42.2431 0.440889C41.9232 0.31495 41.6932 0.243164 41.4694 0.243164C41.2455 0.243164 41.0354 0.315579 40.7557 0.453483L35.4237 3.11963C35.2452 3.20778 35.1553 3.34632 35.1553 3.48107C35.1553 3.63283 35.2452 3.76507 35.4535 3.84315"
							fill="#E30521"
						/>
						<path
							d="M13.5823 15.1663C13.702 15.2922 13.813 15.4018 13.7163 15.6008C13.635 15.7645 13.2624 15.7425 13.2624 15.7425H7.8771C7.04444 14.7601 5.1026 11.9781 5.1026 11.9781H5.05796V15.7425H0V5.53568H5.05796V9.22446H5.1026L7.77294 5.53568H12.7565C12.7565 5.53568 12.9425 5.52939 12.9803 5.67422C13.0175 5.83164 12.8687 5.98907 12.8687 5.98907L9.43888 10.0456C9.43888 10.0456 12.3405 13.9138 13.5823 15.1694"
							fill="black"
						/>
						<path
							d="M19.5919 15.7424C19.5919 15.7424 19.9019 15.7651 19.9943 15.6007C20.0904 15.403 20.0085 15.3023 19.8529 15.2078C19.8529 15.2078 18.2979 14.597 18.2979 11.6576V5.53564H13.3813V11.9938C13.382 13.8533 14.1359 15.7424 16.3201 15.7424H19.5919Z"
							fill="black"
						/>
						<path
							d="M25.7815 13.596C24.9562 13.596 23.617 13.2937 23.617 12.2806C24.0783 12.4437 24.5694 12.523 25.2383 12.523C27.1504 12.523 29.5015 11.8713 29.5015 10.1339C29.5015 8.3651 27.2546 7.21338 24.7926 7.21338C22.0398 7.21338 19.1394 8.63587 19.1394 11.6572C19.1394 14.8327 22.1154 16.0021 25.0294 16.0021C26.6209 16.0021 28.2205 15.7288 29.2318 15.3352L29.4103 13.0091L29.3657 12.9625C28.7265 13.2201 27.045 13.5979 25.7802 13.5979M23.3938 10.0124C23.3938 9.51555 24.1825 9.25675 24.9488 9.25675C25.6699 9.25675 26.407 9.48407 26.407 9.92108C26.407 10.4374 25.601 10.6799 24.8372 10.6799C24.1081 10.6799 23.3938 10.45 23.3938 10.0124Z"
							fill="black"
						/>
						<path
							d="M52.1514 12.9606C51.519 13.2182 49.6819 13.596 48.4171 13.596C47.5993 13.596 46.2527 13.2937 46.2527 12.2806C46.714 12.4437 47.2124 12.523 47.874 12.523C49.7861 12.523 52.1365 11.8713 52.1365 10.1339C52.1365 8.3651 49.8896 7.21338 47.435 7.21338C44.6754 7.21338 41.7744 8.63587 41.7744 11.6572C41.7744 14.8327 44.7504 16.0021 47.6737 16.0021C49.2653 16.0021 51.0056 15.7288 52.0174 15.3352L52.196 13.0091L52.1514 12.9625M46.0295 10.0124C46.0295 9.51555 46.8175 9.25675 47.5795 9.25675C48.3092 9.25675 49.0452 9.48407 49.0452 9.92108C49.0452 10.4374 48.2392 10.6799 47.4834 10.6799C46.7468 10.6799 46.0326 10.45 46.0326 10.0124"
							fill="black"
						/>
						<path
							d="M61.1369 11.6633C61.2337 11.7195 61.3466 11.7406 61.4568 11.7231C61.5808 11.6885 61.6651 11.5972 61.6726 11.4278L61.9999 7.53122C61.6141 7.42698 61.2162 7.37615 60.817 7.3801C59.6049 7.3801 58.4517 7.80199 57.6109 8.53181H57.5142L57.291 7.54382H52.9771V15.7425H57.8862V11.9939C58.2805 11.3579 58.8385 11.1155 59.4362 11.1155C60.0686 11.1155 60.5814 11.3264 61.1394 11.6633"
							fill="black"
						/>
						<path
							d="M37.0661 7.21316C36.4858 7.21316 35.8466 7.36429 35.1441 7.54375V5.53564H30.2275V15.7424H35.8733C39.5703 15.7424 41.3404 13.4327 41.3404 11.2231C41.3404 9.21182 39.8679 7.21379 37.0624 7.21379M37.3675 11.6261C37.3675 12.7337 36.7797 13.2595 36.1994 13.2595C35.6191 13.2595 35.017 12.7337 35.017 11.667V11.3742C35.017 10.2697 35.6042 9.74392 36.1845 9.74392C36.7797 9.74392 37.3675 10.2697 37.3675 11.3364V11.6261Z"
							fill="black"
						/>
					</svg>
				</div>
			{/if}
			{#if headline}
				<p class="text-center mb-0 mt-2 text-sm text-fuel-strong">
					{headline}
				</p>
			{/if}
		</header>
	{/if}
	{#if restStopFacilityIds.length > 0}
		<ul class="flex flex-wrap justify-center py-2 max-w-360px mx-auto gap-2 ps-0 m-0">
			{#each restStopFacilityIds as facilityId (facilityId)}
				{@const { iconClass, text } = restStopFacility[facilityId]}
				<li class="flex flex-col items-center w-80px">
					<div class="bg-white rounded-full p-4 size-14">
						<span class={[iconClass, 'inline-block size-6 text-rest-stop-strong']}></span>
					</div>
					<span class="mt-2 text-sm text-gray-700 text-center">
						{text}
					</span>
				</li>
			{/each}
		</ul>
	{/if}
</section>

<style>
	.facilities-container {
		clip-path: polygon(
			0 0,
			100% var(--offset-height),
			100% 100%,
			0 calc(100% - var(--offset-height))
		);
		background:
			var(--kleber-pattern-url) center / cover,
			radial-gradient(
				143.03% 141.42% at 0% 0%,
				rgba(255, 255, 255, 0) 0%,
				rgba(255, 255, 255, 0) 75.36%,
				rgba(255, 255, 255, 0.4) 100%
			),
			radial-gradient(
				143.03% 141.42% at 100% 100%,
				rgba(255, 255, 255, 0) 0%,
				rgba(255, 255, 255, 0) 50.22%,
				rgba(255, 255, 255, 0.4) 100%
			),
			var(--universe-rest-stop-weakest, #e9eef5);
	}
</style>
